/** The stage: one hero, and the hero changes with the moment (UX §3, §8.1).
 *
 * - Between questions the hero is **the next question's text** — the thing the teacher is
 *   about to say.
 * - While a question is open the hero is **how many have answered**. The question sits above
 *   it as a label: the teacher just said it out loud and the room can see it on the projector.
 *   The owner asked to keep the bars filling beside the count while the room answers, so the
 *   distribution stays on the open face rather than waiting for the close.
 * - After the close the hero is **the bars and the share correct** — the thing the lesson
 *   turns on — with a standing line saying exactly what the students can see.
 *
 * Everything is in rem so the interface magnifier carries it; the three sizes come from
 * container queries in styles.css, not from device breakpoints.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { DistributionBars, DraftChip, Qr, Timer } from "../ui";
import type { Snapshot } from "./useLive";
import { AnsweringMeter } from "./AnsweringMeter";
import { ChannelLine, useChannelAnnouncement } from "./ChannelLine";

export type Face = "loading" | "poster" | "upNext" | "asking" | "result" | "empty" | "ended";

type Props = {
  face: Face;
  snapshot: Snapshot | null;
  /** The card selected in Next, for the up-next face. */
  upNext: { stem: string; lo: string | null; type: string; draft: boolean; step: number | null } | null;
  loCode: (loId: string | null | undefined) => string | null;
  joinCode: string;
  joinUrl: string;
  skewMs: number;
  quiet: boolean;
  /** Dimmed and not driveable: the room is out of reach for a moment. */
  dimmed: boolean;
  /** The course's `telegram` policy on this session (off | notify | answer), once known. */
  telegram?: string | null;
  /** The session is hosted by the Teacher Computer app, which has no internet (FR-056). */
  localHost?: boolean;
  children?: React.ReactNode;
};

export function LiveStage({ face, snapshot, upNext, loCode, joinCode, joinUrl, skewMs, quiet, dimmed, telegram, localHost, children }: Props) {
  const { t } = useTranslation();
  const item = snapshot?.item ?? null;
  const agg = snapshot?.aggregate ?? null;
  const present = snapshot?.present ?? 0;
  const channels = snapshot?.channels;
  const stage = useRef<HTMLElement>(null);

  // The lobby notice (FR-056): the channel answers over the internet, so on a local host it
  // is off for this lesson and the sentence says why ("it needs the internet"); a cloud
  // session whose channel has gone dark (the bot down, the Telegram API refusing) has the
  // internet, so it gets its own sentence, "not reachable right now", and never sends the
  // teacher after a connectivity problem the room does not have. Stated on the poster, with
  // the number of the course's students it affects when the frame carries one; a frame with
  // no `course` block (a host whose cache is stale) states it without a number rather than
  // inventing one, and a course with nobody on the channel states only that it is off. The
  // Teacher Computer app never claims the channel is available.
  const telegramName = t("live.channel.names.telegram");
  const cause = telegram !== "answer" ? null : localHost === true ? "offline" : channels?.state?.telegram === "unreachable" ? "unreachablePoster" : null;
  const affected = channels?.course?.telegram;
  const offlineNotice = cause
    ? affected === undefined
      ? t(`live.channel.${cause}UpTo`, { channel: telegramName })
      : affected === 0
        ? t(`live.channel.${cause}None`, { channel: telegramName })
        : t(`live.channel.${cause}`, { count: affected, channel: telegramName })
    : null;

  // The channel lines' announcement (FR-049): a polite region that stays mounted on every
  // face and is fed a throttled sentence, so a screen reader hears "3 students are still
  // answering in Telegram" once, then at most every few seconds while the count moves, and
  // nothing once it clears. The visible list itself carries no aria-live.
  // Nothing is announced while the face is still loading: the first frame would otherwise
  // be read in the past tense ("may not have this question") a moment before the up-next
  // face states the same fact forward, and the throttle would hold the wrong one for a while.
  const upcoming = face === "upNext" || face === "empty";
  const channelSaid = useChannelAnnouncement(face === "loading" ? undefined : channels, face === "asking", upcoming);

  // One assertive announcement when a question opens, one when it closes, carrying the
  // numbers. The answered count itself is polite and throttled (see the count's own label).
  const said = useRef("");
  const announcement = (() => {
    if (!item) return "";
    if (face === "asking") return `${t("live.a11y.opened")} ${item.snapshot.stem}`;
    if (face === "result" && agg)
      return `${t("live.a11y.closed")} ${t("live.answered", { a: agg.answered, p: present })}${
        agg.scorable > 0 ? ` · ${t("live.correct", { pct: Math.round(100 * (agg.correct_ratio ?? 0)) })}` : ""
      }`;
    return "";
  })();
  useEffect(() => {
    said.current = announcement;
  }, [announcement]);

  const lo = loCode(item?.snapshot.lo_id);
  const spoken = !!item?.snapshot.spoken;

  return (
    <section
      ref={stage}
      data-live-stage
      tabIndex={-1}
      aria-label={t("live.stage.label")}
      className={[
        "flex min-h-[16rem] flex-col gap-4 rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm outline-none sm:p-6",
        dimmed ? "opacity-60" : "",
      ].join(" ")}
    >
      <p className="sr-only" aria-live="assertive">{announcement}</p>
      <p className="sr-only" aria-live="polite" data-channel-announce>{channelSaid}</p>

      {face === "loading" && <p className="text-[color:var(--ink-2)]">{t("app.loading")}</p>}

      {face === "poster" && (
        <div className="board -m-4 flex flex-wrap items-center gap-6 rounded-[var(--radius-card)] p-6 sm:-m-6 sm:p-8">
          {/* First on the board, before the code: it is the one thing the teacher must say
              to the room before anyone scans, and the sticky action bar covers the poster's
              foot on a phone, so the last line of the poster is the first one hidden. */}
          {offlineNotice && (
            <p className="w-full text-sm" data-channel-offline>
              {offlineNotice}
            </p>
          )}
          <div className="shrink-0 rounded-[var(--radius-card)] bg-white p-3">
            <Qr text={joinUrl} size={168} label={t("live.qrLabel")} />
          </div>
          {/* A real minimum, not `min-w-0`: below it the code and the link must drop to their
              own line rather than being squeezed into eighty pixels beside the QR. */}
          <div className="min-w-[14rem] flex-1">
            <p className="text-sm text-[color:var(--ink-2)]">{t("live.joinCode")}</p>
            <p className="bignum wrap-anywhere text-5xl leading-none sm:text-6xl">{joinCode}</p>
            <p className="wrap-anywhere mt-3 text-sm text-[color:var(--ink-2)]">{joinUrl}</p>
            <p className="mt-4 text-lg">{t("live.lobbyHint", { count: present })}</p>
          </div>
        </div>
      )}

      {face === "upNext" && upNext && (
        <>
          <p className="flex flex-wrap items-center gap-2 text-sm text-[color:var(--ink-2)]">
            {upNext.lo && <span>{t("live.objective", { lo: upNext.lo })}</span>}
            {upNext.step !== null && <span>· {t("live.planStep", { n: upNext.step + 1 })}</span>}
            <span>· {t(`editor.types.${upNext.type}`)}</span>
            {upNext.draft && <DraftChip kind="draft" />}
          </p>
          <h2 className="live-hero display">{upNext.stem}</h2>
        </>
      )}

      {(face === "asking" || face === "result") && item && (
        <>
          <p className="flex flex-wrap items-center gap-2 text-sm text-[color:var(--ink-2)]">
            {lo && <span>{t("live.objective", { lo })}</span>}
            {item.basis === "revote" ? (
              <span>· {t("live.stage.revoteRound", { n: item.attempt })}</span>
            ) : item.attempt > 1 ? (
              <span>· {t("live.stage.roundOf", { n: item.attempt })}</span>
            ) : null}
            {spoken && <span>· {t("live.askedAloud")}</span>}
            {face === "result" && <span>· {t("live.stage.closedWord")}</span>}
          </p>
          <h2 className={face === "asking" ? "live-label clamp-3" : "live-label"}>
            {spoken && !item.snapshot.stem ? t("live.aloud.spokenStem") : item.snapshot.stem}
          </h2>
        </>
      )}

      {/* The still-answering line and the failure lines (FR-049, FR-055), on every face:
          a window may still be open on a round the room has left, and the fact is stated
          wherever the teacher is looking, without a warning, a confirm or a nag. */}
      {face !== "loading" && face !== "poster" && <ChannelLine channels={channels} roundOpen={face === "asking"} upcoming={upcoming} />}

      {/* STORY-086: how many pressed the clicker's "I'm lost" chord on this round. The
          count rides on the staff frame only, so this line is the teacher's; it is not an
          answer and is in none of the numbers above or below. */}
      {(face === "asking" || face === "result") && item && (agg?.lost ?? 0) > 0 && (
        <p className="text-sm font-semibold" data-live-lost>
          {t("live.channel.clicker.lost", { count: agg?.lost ?? 0 })}
        </p>
      )}

      {face === "asking" && item && (
        <div className="flex flex-col gap-4">
          <div className="flex items-end justify-between gap-4">
            <AnsweringMeter answered={agg?.answered ?? 0} present={present} />
            <div className="live-clock bignum">
              <Timer deadline={item.deadline} skewMs={skewMs} />
            </div>
          </div>
          {/* Owner decision (2026-09-10): the bars stay on while the room answers. */}
          {agg && agg.options.length > 0 && (
            <DistributionBars
              bars={agg.options.map((o) => ({ key: o.key, label: o.key, count: o.count }))}
              total={Math.max(agg.answered, 1)}
              mode="live"
            />
          )}
        </div>
      )}

      {face === "result" && item && agg && (
        <div className="flex flex-col gap-3">
          {agg.answered === 0 ? (
            <>
              <p className="live-hero display">{t("live.stage.noAnswers")}</p>
              <p className="text-sm text-[color:var(--ink-2)]">
                {present === 0 ? t("live.stage.noAnswersEmptyRoom", { code: joinCode }) : t("live.stage.noAnswersHint")}
              </p>
            </>
          ) : (
            <>
              <DistributionBars
                bars={agg.options.map((o) => ({ key: o.key, label: o.key, count: o.count, correct: o.correct, before: agg.before[o.key] }))}
                total={Math.max(agg.answered, 1)}
                mode={
                  item.revealed === "correct"
                    ? "revealed"
                    : agg.basis === "revote" && Object.keys(agg.before).length
                      ? "paired"
                      : agg.correct_keys?.length
                        ? "revealed"
                        : "closed"
                }
              />
              <p className="text-sm text-[color:var(--ink-2)]">
                {t("live.answered", { a: agg.answered, p: present })}
                {agg.scorable > 0 && ` · ${t("live.correct", { pct: Math.round(100 * (agg.correct_ratio ?? 0)) })}`}
                {!quiet && ` · ${t("live.sure", { n: agg.confidence.sure ?? 0 })}`}
                {agg.keyed && ` · ${t("live.done.keyedBy")}`}
              </p>
              {agg.basis === "revote" && agg.before_correct_ratio != null && agg.correct_ratio != null && (
                <p className="rounded-[var(--radius-control)] bg-[color:var(--surface-2)] px-3 py-2 text-sm">
                  <span className="bignum">{Math.round(100 * agg.before_correct_ratio)}%</span>
                  <span className="mx-2 text-[color:var(--ink-2)]">→</span>
                  <span className="bignum">{Math.round(100 * agg.correct_ratio)}%</span>
                  <span className="ml-2" style={{ color: (agg.revote_delta ?? 0) >= 0 ? "var(--secure)" : "var(--not-yet)" }}>
                    {t("live.revoteDelta", { delta: Math.round(100 * (agg.revote_delta ?? 0)) })}
                  </span>
                </p>
              )}
            </>
          )}
          {/* The standing reminder: nothing is ever silently withheld from the room. */}
          <p className="text-sm">
            <span className="text-[color:var(--ink-2)]">{t("live.reveal.label")}: </span>
            <span className="marker">
              {item.revealed === "correct"
                ? t("live.reveal.correct")
                : item.revealed === "distribution"
                  ? t("live.reveal.distribution")
                  : t("live.reveal.nothing")}
            </span>
          </p>
        </div>
      )}

      {face === "empty" && (
        <>
          <h2 className="live-hero display">{t("live.stage.nothingLeft")}</h2>
          <p className="text-[color:var(--ink-2)]">{t("live.stage.nothingLeftHint")}</p>
        </>
      )}

      {face === "ended" && <h2 className="live-hero display">{t("live.ended")}</h2>}

      {children}
    </section>
  );
}
